<template>
    <!--
        Deals list — TAG portal refresh, variant A.
        TopNav + DealsHeader (title/stats/search/status chips) + DealsTable.
        Clicking a row opens DealDrawer (right slide-out) with the deal's
        money stats + details + links to the full edit record. Behaviour
        (fetchData / search / sortData / column-edit / delete) preserved.
    -->
    <div class="tag-app" :style="appStyle">
        <TopNav active-id="deals" />

        <div :style="bodyStyle">
            <main :style="mainStyle">
                <DealsHeader
                    :total-items="displayItems.length"
                    :statuses="statuses"
                    :selected-statuses="selectedStatus"
                    :without-sales="withoutSales"
                    :search-agency="searchRequest.agency"
                    :search-brand="searchRequest.brand"
                    :show-agency-search="!!gates['deal.filter.agency.view']"
                    :add-href="gates['deal.create'] ? '/deals/create' : null"
                    @search-agency="onSearchAgency"
                    @search-brand="onSearchBrand"
                    @status-all="onStatusAll"
                    @status-toggle="onStatusToggle"
                    @toggle-without-sales="onToggleWithoutSales"
                    @edit-columns="openColumnsModal"
                />

                <div v-if="loading" :style="loaderWrapStyle">
                    <clip-loader :loading="loading" color="#ec4657" size="36px" />
                </div>

                <DealsTable
                    v-else
                    :items="displayItems"
                    :columns="current_columns"
                    :last-sort="lastSort"
                    :descending="descending"
                    :open-id="openDeal ? openDeal.id : null"
                    :can-delete="canDelete"
                    @sort="sortData"
                    @open="onRowClick"
                    @delete="onDeleteDeal"
                />
            </main>

            <DealDrawer v-if="openDeal" :deal="openDeal" @close="closeDrawer" />
        </div>

        <Column_Modal
            :is_show="showModalColumns"
            :__columns="current_columns"
            :selectColumn="selectColumn"
            :closeModal="closeModal"
            :selected_col="selected_col"
            v-if="showModalColumns"
        ></Column_Modal>

        <Confirm_Modal
            v-if="deleteDealId"
            :title="'Are you sure want to delete Deal and all related Sales and financial data?'"
            :applyLabel="'Yes'"
            :secondaryLabel="'No'"
            :onApplyAction="handleConfirmDeleteDeal"
            :onSecondaryAction="handleCancelDeleteDeal"
        />
    </div>
</template>

<script>
import ClipLoader from "vue-spinner/src/ClipLoader.vue";
import axios from "axios";
import * as sortArray from "../../parts/sort-array";
import Column_Modal from "../ColumnsModal/index.vue";
import Confirm_Modal from "../ConfirmModal/index.vue";

import TopNav from "../Agencies/_atoms/TopNav.vue";
import DealsHeader from "./_atoms/DealsHeader.vue";
import DealsTable from "./_atoms/DealsTable.vue";
import DealDrawer from "./_atoms/DealDrawer.vue";

export default {
    name: "Deals",
    props: ["agencies", "statuses", "brands", "gates", "columns", "canDelete"],
    components: {
        ClipLoader,
        Column_Modal,
        Confirm_Modal,
        TopNav,
        DealsHeader,
        DealsTable,
        DealDrawer,
    },
    data() {
        return {
            selectedStatus: [],
            agency: null,
            brand: null,
            withoutSales: false,
            loading: false,
            items: [],
            filterItems: [],
            searchRequest: {
                active: false,
                agency: "",
                brand: "",
            },
            descending: false,
            lastSort: "id",
            showModalColumns: false,
            selected_col: null,
            current_columns: [],
            filter_columns: [],
            deleteDealId: null,
            isInitPage: false,
            // Detail drawer — the deal whose row was clicked (null = closed).
            openDeal: null,
        };
    },

    computed: {
        displayItems() {
            if (this.searchRequest.active) {
                return this.filterItems;
            }
            return this.items;
        },
        // ===== Page chrome styles =====
        appStyle() {
            return {
                height: "100vh",
                background: "var(--bg)",
                color: "var(--text)",
                fontFamily: "var(--font-sans)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            };
        },
        bodyStyle() {
            return { display: "flex", flex: "1 1 auto", minHeight: 0 };
        },
        mainStyle() {
            return {
                flex: "1 1 auto",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                background: "var(--bg)",
            };
        },
        loaderWrapStyle() {
            return {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "60px",
                color: "var(--text-muted)",
            };
        },
    },

    mounted() {
        this.current_columns = JSON.parse(JSON.stringify(this.columns));
        this.fetchData();
    },

    methods: {
        statusBadgeClass(status) {
            const normalizedStatus = String(status || "")
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "");
            return normalizedStatus
                ? `deal-status-badge--${normalizedStatus}`
                : "deal-status-badge--empty";
        },

        // ===== Variant-A header / drawer handlers =====
        // (ref-free equivalents of the legacy checkbox handlers — the new
        // chip UI mutates selectedStatus / withoutSales directly.)
        onSearchAgency(value) {
            this.searchRequest.agency = value;
            this.search();
        },
        onSearchBrand(value) {
            this.searchRequest.brand = value;
            this.search();
        },
        onStatusAll() {
            this.selectedStatus = [];
            this.withoutSales = false;
            this.fetchData();
        },
        onStatusToggle(id) {
            const idStr = String(id);
            const current = this.selectedStatus.map((v) => String(v));
            this.selectedStatus = current.includes(idStr)
                ? current.filter((v) => v !== idStr)
                : [...current, idStr];
            this.fetchData();
        },
        onToggleWithoutSales() {
            this.withoutSales = !this.withoutSales;
            this.fetchData();
        },
        onRowClick(item) {
            this.openDeal = item;
        },
        closeDrawer() {
            this.openDeal = null;
        },

        checkboxValue(e) {
            if (e.target.name !== "withoutSales") {
                if (e.target.checked) {
                    this.selectedStatus.push(e.target.value);
                    if (this.$refs.filter_status_all.checked) {
                        this.$refs.filter_status_all.click();
                    }
                } else {
                    this.selectedStatus = this.selectedStatus.filter(
                        (i) => i != e.target.value
                    );
                }
            } else {
                if (e.target.checked) {
                    this.withoutSales = true;
                    if (this.$refs.filter_status_all.checked) {
                        this.$refs.filter_status_all.click();
                    }
                } else {
                    this.withoutSales = false;
                }
            }
            this.fetchData();
        },

        handleCheckAllStatus(e) {
            if (e.target.checked) {
                this.selectedStatus = [];
                this.withoutSales = false;
                this.statuses.forEach((filter) => {
                    const _checkbox =
                        this.$refs[`filter_status_${filter.id}`][0];
                    if (_checkbox.checked) {
                        _checkbox.click();
                    }
                });
                const _withoutSalesCheckbox = this.$refs.filter_without_sales;
                if (_withoutSalesCheckbox.checked) {
                    _withoutSalesCheckbox.click();
                }
            }
        },

        fetchData() {
            this.loading = true;
            axios
                .post("/deals/getList", {
                    status: this.selectedStatus,
                    brand: this.brand,
                    agency: this.agency,
                    withoutSales: this.withoutSales,
                })
                .then((res) => {
                    this.loading = false;
                    if (res.data.length) {
                        this.items = this.reformatDataByColumns(res.data);
                        if (this.searchRequest.active) {
                            this.search();
                        }
                    } else {
                        this.items = [];
                    }
                })
                .catch(() => {
                    this.loading = false;
                })
                .finally(() => {
                    if (!this.isInitPage) {
                        this.getFilterFromURL();
                        this.isInitPage = true;
                    }
                });
        },

        search() {
            this.searchRequest.active =
                this.searchRequest.agency.length >= 3 ||
                this.searchRequest.brand.length >= 3;

            if (this.searchRequest.active) {
                this.filterItems = this.items.filter(
                    (item) =>
                        (this.searchRequest.agency
                            ? item.agency.name
                                  .toLowerCase()
                                  .includes(
                                      this.searchRequest.agency.toLowerCase()
                                  )
                            : true) &&
                        (this.searchRequest.brand
                            ? item.brand.name
                                  .toLowerCase()
                                  .includes(
                                      this.searchRequest.brand.toLowerCase()
                                  )
                            : true)
                );
            }
        },

        sortData(sortBy) {
            let self = this;
            if (self.lastSort !== sortBy) self.descending = false;
            self.lastSort = sortBy;
            switch (sortBy) {
                case "id":
                    self.items = self.items.sort(
                        sortArray.sortNumber("id", self.descending)
                    );
                    break;
                case "date":
                    self.items = self.items.sort(
                        sortArray.sortDate("date", self.descending, (a) =>
                            a.toUpperCase()
                        )
                    );
                    break;
                case "name":
                    self.items = self.items.sort(
                        sortArray.sortText(
                            "name",
                            self.descending,
                            (a) => a.toUpperCase(),
                            "name"
                        )
                    );
                    break;
                case "agency":
                    self.items = self.items.sort(
                        sortArray.sortText(
                            "agency",
                            self.descending,
                            (a) => a.toUpperCase(),
                            "name"
                        )
                    );
                    break;
                case "partner":
                    self.items = self.items.sort(
                        sortArray.sortText(
                            "partner",
                            self.descending,
                            (a) => a.toUpperCase(),
                            "name"
                        )
                    );
                    break;
                case "type":
                    self.items = self.items.sort(
                        sortArray.sortText("type", self.descending, (a) =>
                            a.toUpperCase()
                        )
                    );
                    break;
                case "status":
                    self.items = self.items.sort(
                        sortArray.sortText("status", self.descending, (a) =>
                            a.toUpperCase()
                        )
                    );
                    break;
                case "amount_invoiced":
                    self.items = self.items.sort(
                        sortArray.sortMoney("amount_invoiced", self.descending)
                    );
                    break;
                case "amount_collect":
                    self.items = self.items.sort(
                        sortArray.sortMoney("amount_collect", self.descending)
                    );
                    break;
                case "commission":
                    self.items = self.items.sort(
                        sortArray.sortMoney("commission", self.descending)
                    );
                    break;
            }
            self.descending = !self.descending;

            if (this.searchRequest.active) {
                this.search();
            }
        },

        reformatDataByColumns(__items = []) {
            try {
                if (__items.length >= 1) {
                    const newFormatData = [];
                    if (this.current_columns.length >= 1) {
                        __items.forEach((item) => {
                            const tempObj = {};
                            this.current_columns
                                .sort((a, b) => a.position - b.position)
                                .forEach((c) => {
                                    if (c.status === "hidden") return;
                                    if (c.slug === "deals") {
                                        tempObj[`${c.slug}`] =
                                            item.year_deals +
                                            "(" +
                                            item.total_deals +
                                            ")";
                                        return;
                                    }
                                    if (c.slug === "invoiced") {
                                        tempObj[`${c.slug}`] =
                                            item.year_brands +
                                            "(" +
                                            item.total_brands +
                                            ")";
                                        return;
                                    }
                                    tempObj[`${c.slug}`] = item[c.slug];
                                    tempObj["brand"] = item["brand"];
                                });
                            newFormatData.push(tempObj);
                        });
                        return newFormatData;
                    }
                }
                return [];
            } catch (err) {
                console.error("reformatDataByColumns error", err);
            }
        },

        openColumnsModal() {
            this.showModalColumns = true;
            document.querySelector("html").style = "overflow-y: hidden";
            document.querySelector("body").style =
                "overflow-y: hidden; margin-right: 16px";
        },

        async closeModal(items = []) {
            await axios
                .post("/deals/updateColumns", { columns: items })
                .then(() => {
                    this.showModalColumns = false;
                    this.current_columns.length = 0;
                    this.current_columns = JSON.parse(JSON.stringify(items));
                    document.querySelector("html").style = "";
                    document.querySelector("body").style = "";
                })
                .catch((err) => {
                    console.error("Axios update columns error", err);
                });
        },

        selectColumn(col) {
            this.selected_col = col;
        },

        onDeleteDeal(id) {
            this.deleteDealId = id;
        },

        handleConfirmDeleteDeal() {
            if (this.deleteDealId) {
                axios
                    .get(`/deals/delete/${this.deleteDealId}`)
                    .then(() => {
                        this.deleteDealId = null;
                        this.fetchData();
                    })
                    .catch((err) =>
                        console.error("handleConfirmDeleteDeal", err)
                    );
            }
        },

        handleCancelDeleteDeal() {
            this.deleteDealId = null;
        },

        getFilterFromURL() {
            let params = new URLSearchParams(window.location.search);
            if (params) {
                let agency_name = params.get("agency");
                if (agency_name) {
                    this.searchRequest.agency = agency_name.toString();
                    this.search();
                    this.$forceUpdate();
                }
            }
        },
    },
};
</script>
